import { AfterViewInit, Component, Input, Optional, Self, ViewChild } from '@angular/core';
import { isControlValid } from '../utils';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { MuiIconOption } from './mui-icon-option';
import { MuiIconFont } from '../../icon';

let nextUniqueId = 0;

@Component({
  selector: 'mui-icon-picker',
  templateUrl: './mui-icon-picker.component.html',
  styleUrls: ['./mui-icon-picker.component.scss']
})
export class MuiIconPicker implements ControlValueAccessor, AfterViewInit {
  @Input()
  icons: MuiIconOption[] = [];

  @Input()
  allowNoSelection = false;

  @Input()
  label = '';

  @Input()
  id = 'mui-icon-picker-' + nextUniqueId++;

  @Input()
  animation = true;

  @ViewChild(NgbPopover)
  iconPicker!: NgbPopover;

  keySelectedElement: number | null = null;

  protected get popoverId(): string {
    return this.id + '-popover';
  }

  readonly deselectOptionIcon: MuiIconOption = { font: MuiIconFont.FONTAWESOME, name: 'ban' };

  get isOpen(): boolean {
    return this._isOpen;
  }

  get displayedOptions(): Array<MuiIconOption | null> {
    const options: Array<MuiIconOption | null> = [...this.icons];

    if (this.allowNoSelection) {
      options.unshift(null);
    }

    return options;
  }

  get value(): MuiIconOption | null {
    return this._currentValue;
  }

  get valid(): boolean {
    return this._control ? isControlValid(this._control) : true;
  }

  get firstSelectionIndex(): number {
    if (!this._currentValue) {
      return 0;
    }

    const index = this.icons.findIndex((option: MuiIconOption) => {
      return option.name === this._currentValue?.name;
    });

    // Add 1 to the index to account for the current selected option
    return index + 1;
  }

  private _currentValue: MuiIconOption | null = null;
  private _isOpen = false;

  constructor(@Self() @Optional() protected _control: NgControl) {
    if (_control) {
      _control.valueAccessor = this;
    }
  }

  ngAfterViewInit(): void {
    if (!this.icons) {
      throw new Error('MuiIconPicker: Property "icons" is required for the icon picker to render');
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.registerOnTouched = fn;
  }

  writeValue(value: MuiIconOption | null): void {
    this._currentValue = value;
  }

  onChange(newOption: MuiIconOption | null): void {
    this._currentValue = newOption;
    this.propagateChange(this._currentValue);
    this.propagateTouch();
    this.closePicker();
  }

  closePicker(): void {
    const popover = document.getElementById(this.id);
    if (popover?.contains(document.activeElement)) {
      // Do not close the popover if the focus is still inside
      return;
    }

    this._control?.control?.markAsTouched();
    this.iconPicker.close(this.animation);
    this._isOpen = false;
  }

  onOptionClick($event: MouseEvent, option: MuiIconOption | null): void {
    $event.stopPropagation();
    this.onChange(option);
    this.keySelectedElement = null;
  }

  selectNextElement(direction: 'left' | 'right', $event: Event): void {
    $event.preventDefault();
    // get all icons and clear the styling

    const iconList = document.querySelectorAll('.mui-icon-picker__option');
    iconList.forEach((icon) => {
      icon.classList.remove('keyselected');
    });

    if (iconList.length > 0) {
      // if the selection wasn't initialized, do it
      if (this.keySelectedElement === null) {
        if (this._currentValue) {
          this.keySelectedElement = this.firstSelectionIndex;
        } else {
          this.keySelectedElement = 0;
          return;
        }
      }

      if (direction === 'right') {
        // if it goes right, select the first element or the next sibling, if it's the end of the line, go to 0
        if (this.keySelectedElement >= iconList.length - 1) {
          this.keySelectedElement = 0;
        } else {
          this.keySelectedElement++;
        }
      } else if (direction === 'left') {
        // if it goes left, select the first element or the next sibling, if it's the end of the line, go to 0
        if (this.keySelectedElement === 0) {
          this.keySelectedElement = iconList.length - 1;
        } else {
          this.keySelectedElement--;
        }
      }
      iconList[this.keySelectedElement].classList.add('keyselected');
    }
  }

  onElementSelect($event: Event): void {
    $event.preventDefault();
    if (this.keySelectedElement !== null) {
      const element: HTMLElement = document.querySelectorAll('.mui-icon-picker__option')[this.keySelectedElement] as HTMLElement;
      element.click();
      this.keySelectedElement = null;
    } else {
      this.openPicker();
    }
  }

  openPicker(): void {
    if (this.isOpen) {
      return;
    }
    this.iconPicker.open();
    this._isOpen = true;
  }

  isSelectedOption(option: MuiIconOption | null): boolean {
    return option && this._currentValue ? option.font === this._currentValue.font && option.name === this._currentValue.name : false;
  }

  setDisabledState(_: boolean): void {
    /* noop */
  }

  private propagateChange = (_: MuiIconOption | null): void => {
    /* noop */
  };
  private propagateTouch = (): void => {
    /* noop */
  };
}
